/**
 * QUIL — Entry point
 */

import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './styles/global.css'
import { App } from './App'
import { AuthProvider } from './lib/AuthProvider'
import { configureErrorSync, installErrorHandlers } from './lib/errorLog'
import { recordSessionError } from './lib/errorSession'
import { noteAppOpened } from './lib/expectations'
import { APP_ID as ERROR_SYNC_APP } from './lib/healthChecks'

// Error sync: every uncaught error, unhandled rejection and ErrorBoundary crash is
// classified as sync noise or a real error, buffered in localStorage, and flushed to
// /Apps/Quil/errors-<deviceId>.md after the next successful sync (sync.ts supplies the
// OneDrive transport once it has a token getter). Design: RESUME-ERROR-SYNC.md in the
// claude-skills-and-routines repo; the three lib modules are canonical copies shared
// with the sibling apps.
// onRecord mirrors every captured entry into errorSession's in-memory, this-tab-only
// list (canonical module, F2) so Settings can show a readable "what went wrong" list
// without waiting for a flush to OneDrive.
configureErrorSync({
  app: ERROR_SYNC_APP,
  build: __BUILD_SHA__,
  describeDevice: () => navigator.userAgent,
  onRecord: recordSessionError,
})
installErrorHandlers()
noteAppOpened(ERROR_SYNC_APP)

// DEV-only e2e hooks (T4): staged conflict / lazy-prose state for Playwright.
// Dynamically imported behind the DEV guard so it never reaches a prod bundle.
if (import.meta.env.DEV) {
  void import('./lib/testHooks').then(m => m.installTestHooks())
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <AuthProvider>
      <App />
    </AuthProvider>
  </StrictMode>,
)
